import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';

/* ============================================================
   SVG ICON HELPERS
   ============================================================ */
const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="#C4915A" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" style={{ width: 12, height: 12 }}>
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const StarIcon = () => (
  <svg viewBox="0 0 24 24" fill="#C4915A" style={{ width: 16, height: 16 }}>
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

/* ============================================================
   FADE-UP OBSERVER HOOK
   ============================================================ */
const useFadeUp = () => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) el.classList.add('visible'); },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return ref;
};

const FadeUp: React.FC<{ children: React.ReactNode; className?: string; style?: React.CSSProperties }> = ({ children, className = '', style }) => {
  const ref = useFadeUp();
  return <div ref={ref} className={`fade-up ${className}`} style={style}>{children}</div>;
};

/* ============================================================
   SERVICE DATA
   ============================================================ */
const services = [
  {
    title: 'Annual Accounts',
    desc: 'Preparation of annual accounts:\n• Limited companies\n• Sole traders\n• Partnerships',
    icon: <svg fill="none" stroke="#C4915A" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>,
  },
  {
    title: 'Tax Returns',
    desc: 'Submission of:\n• Self-assessment tax returns\n• Corporation tax\n• Capital gains tax\n• Making Tax Digital quarterly updates',
    icon: <svg fill="none" stroke="#C4915A" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>,
  },
  {
    title: 'VAT & CIS Returns',
    desc: 'Timely and accurate VAT return preparation and submission, plus Construction Industry Scheme returns for contractors and subcontractors.',
    icon: <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><text x="12" y="18" textAnchor="middle" fontFamily="DM Serif Display, serif" fontSize="24" fontWeight="700" fill="#C4915A">£</text></svg>,
  },
  {
    title: 'Company Formation',
    desc: 'Complete company formation service including registration with Companies House, HMRC, setting up PAYE schemes, and ongoing compliance support.',
    icon: <svg fill="none" stroke="#C4915A" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="8.5" cy="7" r="4"/><line x1="20" y1="8" x2="20" y2="14"/><line x1="23" y1="11" x2="17" y2="11"/></svg>,
  },
  {
    title: 'Management Accounts',
    desc: 'Regular management accounts that give you a clear picture of your business performance, helping you make informed decisions with confidence.',
    icon: <svg fill="none" stroke="#C4915A" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>,
  },
  {
    title: 'Advisory Services',
    desc: 'Strategic financial advice to help your business grow — from tax planning and budgeting to business structuring and succession planning.',
    icon: <svg fill="none" stroke="#C4915A" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>,
  },
];

/* ============================================================
   TESTIMONIAL DATA (placeholders)
   ============================================================ */
const testimonials = [
  {
    quote: 'I have worked with Bantock Accounting for 3 years now. They have assisted me from self employed all the way up to owning multiple businesses. Both Mike and Jessie give efficient, accurate and helpful information for every single enquiry and do genuinely care about their clients. They are an amazing family run business and I have referred everyone friend or family member who needs an accountant. There are many uncertainties in life, however whenever someone asks; \'have you got a good accountant\', I have not once hesitated to express how amazing they are.',
    name: 'Chiropractor Clinic',
    role: 'Ipswich',
  },
  {
    quote: 'We have used Bantock Accountancy for both our previous business and our current company, and the service has consistently been excellent. Jessica, has been excellent, highly knowledgeable and efficient. She responds quickly, provides clear guidance, and ensures everything is handled smoothly. We would highly recommend Bantock Accountancy to anyone looking for reliable and professional business.',
    name: 'Company Director',
    role: 'Ipswich',
  },
  {
    quote: 'From the very beginning, Michael and Jessie have been professional, responsive, and incredibly knowledgeable. They took the time to understand my specific needs and provided clear, practical advice that has made a real difference to my finances. What really stands out is their attention to detail and commitment to accuracy. Everything is handled efficiently and transparently, with complex financial matters explained in a way that is easy to understand. Overall, I would highly recommend their services to anyone looking for reliable, trustworthy, and expert accounting support. They go above and beyond to ensure their clients are well taken care of.',
    name: 'Plumbing & Heating Engineer',
    role: 'Ipswich',
  },
  {
    quote: 'I can\'t recommend Jessie at Bantock Advisory highly enough. From the very beginning of my journey setting up as a sole trader in the catering industry, Jessie has been absolutely fantastic. What initially felt like an overwhelming process quickly became manageable thanks to her clear guidance, patience, and genuine support.\n\nJessie took the time to explain everything in a way that made sense, ensuring I felt confident and in control of my business decisions. She was always available to answer questions, no matter how small, and consistently went above and beyond to make sure everything was set up correctly from the start.\n\nMore recently, Jessie has been an incredible support in guiding me through the process of applying for maternity allowance. Her knowledge and attention to detail made what could have been a stressful experience feel straightforward and well-organised.\n\nBantock Advisory as a whole have been equally impressive\u2014professional, reliable, and truly supportive of their clients. It\'s clear they genuinely care about the people they work with. I feel very fortunate to have Jessie and the team supporting me, and I wouldn\'t hesitate to recommend them to anyone looking for expert, friendly, and dependable accounting services.',
    name: 'Private Chef',
    role: 'Suffolk',
  },
];

/* ============================================================
   TRUNCATED QUOTE COMPONENT
   ============================================================ */
const CHAR_LIMIT = 280;

const TruncatedQuote: React.FC<{ text: string }> = ({ text }) => {
  const [expanded, setExpanded] = useState(false);
  const needsTruncation = text.length > CHAR_LIMIT;

  const displayText = !needsTruncation || expanded
    ? text
    : text.slice(0, CHAR_LIMIT).replace(/\s+\S*$/, '') + '...';

  return (
    <blockquote style={{
      fontSize: '1.05rem',
      color: 'rgba(255,255,255,0.7)',
      lineHeight: 1.8,
      marginBottom: 28,
      fontStyle: 'italic',
      textAlign: 'center',
      whiteSpace: 'pre-line',
    }}>
      "{displayText}"
      {needsTruncation && (
        <button
          onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
          style={{
            display: 'inline',
            background: 'none',
            border: 'none',
            color: 'var(--amber)',
            cursor: 'pointer',
            fontStyle: 'normal',
            fontWeight: 600,
            fontSize: '0.9rem',
            marginLeft: 6,
            padding: 0,
            transition: 'opacity 0.3s ease',
          }}
        >
          {expanded ? 'Read less' : 'Read more'}
        </button>
      )}
    </blockquote>
  );
};

/* ============================================================
   TESTIMONIAL CAROUSEL
   ============================================================ */
interface TestimonialCarouselProps {
  testimonials: { quote: string; name: string; role: string }[];
}

const TestimonialCarousel: React.FC<TestimonialCarouselProps> = ({ testimonials }) => {
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const total = testimonials.length;

  const goTo = useCallback((index: number) => {
    setCurrent(((index % total) + total) % total);
  }, [total]);

  const next = useCallback(() => goTo(current + 1), [current, goTo]);
  const prev = useCallback(() => goTo(current - 1), [current, goTo]);

  // Auto-advance every 8 seconds (pauses on interaction)
  useEffect(() => {
    if (paused) return;
    const timer = setInterval(next, 8000);
    return () => clearInterval(timer);
  }, [next, paused]);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchEndX.current = e.touches[0].clientX;
  };
  const handleTouchMove = (e: React.TouchEvent) => { touchEndX.current = e.touches[0].clientX; };
  const handleTouchEnd = () => {
    const diff = touchStartX.current - touchEndX.current;
    // Only trigger slide if it was a clear horizontal swipe (> 60px), not a tap
    if (Math.abs(diff) < 60) return;
    if (diff > 0) { next(); setPaused(true); }
    if (diff < 0) { prev(); setPaused(true); }
  };

  return (
    <div style={{ position: 'relative', maxWidth: 700, margin: '0 auto' }}>
      {/* Carousel track */}
      <div
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{ overflow: 'hidden', borderRadius: 'var(--radius-lg)' }}
      >
        <div style={{
          display: 'flex',
          transition: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
          transform: `translateX(-${current * 100}%)`,
        }}>
          {testimonials.map((t, i) => (
            <div key={i} style={{
              minWidth: '100%',
              padding: '40px 36px',
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.06)',
              borderRadius: 'var(--radius-lg)',
              boxSizing: 'border-box',
            }}>
              <TruncatedQuote text={t.quote} key={`quote-${i}-${current}`} />
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontWeight: 600, fontSize: '0.95rem', color: 'var(--amber)' }}>{t.name}</div>
                <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.35)', marginTop: 2 }}>{t.role}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Arrow buttons */}
      {total > 1 && (
        <>
          <button onClick={() => { prev(); setPaused(true); }} aria-label="Previous testimonial" style={{
            position: 'absolute', top: '50%', left: -20, transform: 'translateY(-50%)',
            width: 40, height: 40, borderRadius: '50%', border: 'none',
            background: 'var(--amber)', color: 'var(--charcoal)', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '1.3rem', fontWeight: 700, transition: 'all 0.3s ease',
            boxShadow: '0 2px 12px rgba(0,0,0,0.2)',
          }}>‹</button>
          <button onClick={() => { next(); setPaused(true); }} aria-label="Next testimonial" style={{
            position: 'absolute', top: '50%', right: -20, transform: 'translateY(-50%)',
            width: 40, height: 40, borderRadius: '50%', border: 'none',
            background: 'var(--amber)', color: 'var(--charcoal)', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '1.3rem', fontWeight: 700, transition: 'all 0.3s ease',
            boxShadow: '0 2px 12px rgba(0,0,0,0.2)',
          }}>›</button>
        </>
      )}

      {/* Dots */}
      {total > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 28 }}>
          {testimonials.map((_, i) => (
            <button
              key={i}
              onClick={() => { goTo(i); setPaused(true); }}
              aria-label={`Go to testimonial ${i + 1}`}
              style={{
                width: i === current ? 28 : 10,
                height: 10,
                borderRadius: 5,
                border: 'none',
                background: i === current ? 'var(--amber)' : 'rgba(255,255,255,0.15)',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

/* ============================================================
   HOME PAGE COMPONENT
   ============================================================ */
const HomePage: React.FC = () => {

  return (
    <>
      <Helmet>
        <title>Bantock Accounting and Advisory Ltd | Chartered Certified Accountants, Ipswich</title>
        <meta name="description" content="ACCA qualified accountants in Ipswich, Suffolk. Tax returns, company accounts, VAT, CIS, and advisory services." />
      </Helmet>

      {/* ===== HERO ===== */}
      <section style={{
        padding: '160px 24px 100px',
        background: 'linear-gradient(165deg, #2C2C2E 0%, #3A3A3D 55%, #4A4A4F 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Decorative warm glows */}
        <div style={{ position: 'absolute', top: -200, right: -200, width: 700, height: 700, borderRadius: '50%', background: 'radial-gradient(circle, rgba(196,145,90,0.06) 0%, transparent 70%)' }} />
        <div style={{ position: 'absolute', bottom: -100, left: -100, width: 500, height: 500, borderRadius: '50%', background: 'radial-gradient(circle, rgba(196,145,90,0.04) 0%, transparent 70%)' }} />

        <div className="hero-grid" style={{ maxWidth: 1200, margin: '0 auto', position: 'relative', zIndex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 60, alignItems: 'center' }}>
          <div>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              background: 'rgba(196,145,90,0.1)',
              border: '1px solid rgba(196,145,90,0.2)',
              borderRadius: 100,
              padding: '6px 16px',
              fontSize: '0.8rem',
              fontWeight: 600,
              color: 'var(--amber-light)',
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              marginBottom: 24,
            }}>
              ACCA Chartered Certified Accountants
            </div>

            <h1 style={{
              fontFamily: 'var(--font-heading)',
              fontSize: 'clamp(2.4rem, 5vw, 3.6rem)',
              color: 'var(--white)',
              lineHeight: 1.15,
              marginBottom: 24,
            }}>
              Accounting for <em style={{ fontStyle: 'normal', color: 'var(--amber)' }}>your business</em>
            </h1>

            <p style={{ fontSize: '1.1rem', color: 'rgba(255,255,255,0.6)', lineHeight: 1.8, maxWidth: 520, marginBottom: 36 }}>
              We provide tailored financial solutions for sole traders, partnerships, and limited companies.
              </p>
             <p style={{ fontSize: '1.1rem', color: 'rgba(255,255,255,0.6)', lineHeight: 1.8, maxWidth: 520, marginBottom: 36 }}>
              From first idea to exit strategy, we support businesses at every stage of their journey — whether you're launching a new venture, scaling up, or planning your next chapter.
             </p>

            <div className="hero-buttons" style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <a href="#contact" className="btn btn-primary">
                <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" /></svg>
                Book a Free Consultation
              </a>
              <a href="#services" className="btn btn-outline">Our Services</a>
            </div>

            {/* Desktop stats row */}
            <div className="hero-stats-desktop" style={{ display: 'flex', gap: 40, marginTop: 48, paddingTop: 32, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
              {[
                { value: 'ACCA', label: 'Fully Qualified' },
                { value: 'Proactive', label: 'Advice & Support' },
                { value: 'Suffolk', label: 'Based Locally' },
              ].map(s => (
                <div key={s.label} style={{ textAlign: 'center' }}>
                  <div style={{ fontFamily: 'var(--font-heading)', fontSize: '2rem', color: 'var(--amber)' }}>{s.value}</div>
                  <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.08em', marginTop: 4 }}>{s.label}</div>
                </div>
              ))}
            </div>

            {/* Mobile: photo + stats side by side */}
            <div className="hero-stats-mobile" style={{ display: 'none', marginTop: 40, paddingTop: 32, borderTop: '1px solid rgba(255,255,255,0.06)', gap: 24, alignItems: 'center' }}>
              <img
                src="/images/hero.jpg"
                alt="Jessica Bantock"
                style={{
                  width: 140,
                  height: 180,
                  objectFit: 'cover',
                  objectPosition: 'center top',
                  borderRadius: 'var(--radius-lg)',
                  border: '2px solid rgba(196,145,90,0.15)',
                  flexShrink: 0,
                }}
              />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {[
                  { value: 'ACCA', label: 'Fully Qualified' },
                  { value: 'Proactive', label: 'Advice & Support' },
                  { value: 'Suffolk', label: 'Based Locally' },
                ].map(s => (
                  <div key={s.label}>
                    <div style={{ fontFamily: 'var(--font-heading)', fontSize: '1.5rem', color: 'var(--amber)' }}>{s.value}</div>
                    <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.08em', marginTop: 2 }}>{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Hero photo */}
          <div className="hero-visual" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <div style={{ width: '100%', maxWidth: 480, position: 'relative' }}>
              <div style={{
                position: 'absolute', top: -16, right: -16, width: 200, height: 200,
                borderRadius: '50%', background: 'radial-gradient(circle, rgba(196,145,90,0.12) 0%, transparent 70%)',
                zIndex: 0,
              }} />
              <div style={{
                position: 'absolute', bottom: -12, left: -12, width: 140, height: 140,
                borderRadius: '50%', background: 'radial-gradient(circle, rgba(196,145,90,0.08) 0%, transparent 70%)',
                zIndex: 0,
              }} />
              <img
                src="/images/hero.jpg"
                alt="Jessica Bantock — Bantock Accounting and Advisory Ltd"
                style={{
                  width: '100%',
                  height: 520,
                  objectFit: 'cover',
                  objectPosition: 'center top',
                  borderRadius: 'var(--radius-lg)',
                  border: '2px solid rgba(196,145,90,0.15)',
                  boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
                  position: 'relative',
                  zIndex: 1,
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* ===== SERVICES ===== */}
      <section className="section" id="services" style={{ background: 'var(--white)' }}>
        <div className="container">
          <FadeUp>
            <div className="section-header">
              <div className="section-label">What We Do</div>
              <h2 className="section-title">Comprehensive Accounting &amp; Tax Services</h2>
              <p className="section-subtitle">From day-to-day bookkeeping to strategic advisory, we offer a complete set of financial services tailored to your needs.</p>
            </div>
          </FadeUp>
          <div className="services-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24 }}>
            {services.map(s => (
              <FadeUp key={s.title}>
                <div className="service-card" style={{
                  background: 'var(--cream)',
                  borderRadius: 'var(--radius-lg)',
                  padding: '36px 28px',
                  border: '1px solid var(--border)',
                  transition: 'all 0.3s ease',
                  height: '100%',
                }}>
                  <div style={{
                    width: 52, height: 52,
                    background: 'linear-gradient(135deg, #2C2C2E 0%, #3A3A3D 100%)',
                    borderRadius: 12,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    marginBottom: 20,
                  }}>
                    <div style={{ width: 24, height: 24 }}>{s.icon}</div>
                  </div>
                  <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.25rem', color: 'var(--text-dark)', marginBottom: 12 }}>{s.title}</h3>
                  <p style={{ fontSize: '0.9rem', color: 'var(--text-mid)', lineHeight: 1.7, whiteSpace: 'pre-line' }}>{s.desc}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ===== ABOUT ===== */}
      <section className="section" id="about" style={{ background: 'var(--cream)' }}>
        <div className="container">
          <div className="about-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 80, alignItems: 'center' }}>
            <FadeUp>
              <div className="section-label" style={{ textAlign: 'left' }}>Who We Are</div>
              <h2 className="section-title" style={{ textAlign: 'left' }}>A Personal Approach to Professional Accounting</h2>
              <p style={{ fontSize: '1rem', color: 'var(--text-mid)', lineHeight: 1.8, marginBottom: 20 }}>
                Bantock Accounting and Advisory Ltd is built on the belief that great accounting should be personal. Based in Ipswich, Suffolk, we offer a dedicated, client-focused service to individuals and businesses alike.
              </p>
              <p style={{ fontSize: '1rem', color: 'var(--text-mid)', lineHeight: 1.8, marginBottom: 20 }}>
                We take the time to understand your unique circumstances, return your calls, and keep things straightforward. Whether you're a sole trader just starting out, a growing limited company, or an individual dealing with a complex capital gain, we provide tailored guidance every step of the way.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 32 }}>
                {[
                  { label: 'ACCA Qualified', icon: <svg width="18" height="18" fill="none" stroke="#C4915A" strokeWidth={2} viewBox="0 0 24 24"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg> },
                  { label: 'Xero Advisor', icon: <svg width="18" height="18" fill="none" stroke="#C4915A" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M18 10h-1.26A8 8 0 109 20h9a5 5 0 000-10z"/></svg> },
                  { label: 'Based in Ipswich', icon: <svg width="18" height="18" fill="none" stroke="#C4915A" strokeWidth={2} viewBox="0 0 24 24"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg> },
                  { label: 'Free Parking', icon: <svg width="18" height="18" fill="none" stroke="#C4915A" strokeWidth={2} viewBox="0 0 24 24"><rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg> },
                ].map(h => (
                  <div key={h.label} style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px',
                    background: 'var(--white)', borderRadius: 'var(--radius)', border: '1px solid var(--border)',
                  }}>
                    <div style={{
                      width: 36, height: 36,
                      background: 'linear-gradient(135deg, rgba(196,145,90,0.12), rgba(196,145,90,0.04))',
                      borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>{h.icon}</div>
                    <span style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-dark)' }}>{h.label}</span>
                  </div>
                ))}
              </div>
            </FadeUp>

            <FadeUp>
              <div style={{ position: 'relative' }}>
                <div style={{ position: 'absolute', top: -20, right: -20, width: 180, height: 180, background: 'radial-gradient(circle, rgba(196,145,90,0.08) 0%, transparent 70%)', borderRadius: '50%' }} />
                <div style={{
                  background: 'var(--white)',
                  borderRadius: 'var(--radius-lg)',
                  padding: '48px 40px',
                  border: '1px solid var(--border)',
                  boxShadow: 'var(--shadow-md)',
                  textAlign: 'center',
                  position: 'relative',
                }}>
                  <img
                    src="/images/jessica-bantock.jpg"
                    alt="Jessica Bantock"
                    style={{
                      width: 120, height: 120,
                      objectFit: 'cover',
                      objectPosition: 'center top',
                      borderRadius: '50%',
                      margin: '0 auto 24px',
                      display: 'block',
                      border: '3px solid var(--amber-pale)',
                    }}
                  />
                  <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.5rem', color: 'var(--text-dark)', marginBottom: 4 }}>Jessica Bantock</h3>
                  <div style={{ fontSize: '0.85rem', color: 'var(--amber)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>Director</div>
                  <div style={{ fontSize: '0.85rem', color: 'var(--text-light)', marginBottom: 20 }}>ACCA — Chartered Certified Accountant</div>
                  <p style={{ fontSize: '0.9rem', color: 'var(--text-mid)', lineHeight: 1.7 }}>
                    With extensive experience in accounting and a deep commitment to client service, Jessica leads Bantock Accounting and Advisory Ltd with a focus on delivering personalised, attentive financial support to every client.
                  </p>
                </div>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* ===== TESTIMONIALS ===== */}
      <section className="section" id="testimonials" style={{ background: 'var(--charcoal)' }}>
        <div className="container">
          <FadeUp>
            <div className="section-header">
              <div className="section-label">Testimonials</div>
              <h2 className="section-title" style={{ color: 'var(--white)' }}>What Our Clients Say</h2>
              <p className="section-subtitle" style={{ color: 'rgba(255,255,255,0.45)' }}>We pride ourselves on building lasting relationships with our clients.</p>
            </div>
          </FadeUp>
          <TestimonialCarousel testimonials={testimonials} />
        </div>
      </section>

      {/* ===== CONTACT ===== */}
      <section className="section" id="contact" style={{ background: 'var(--cream)' }}>
        <div className="container">
          <FadeUp>
            <div className="section-header">
              <div className="section-label">Get in Touch</div>
              <h2 className="section-title">Let's Talk About Your Business</h2>
              <p className="section-subtitle">Book a free initial consultation. We're always happy to have a chat about how we can help.</p>
            </div>
          </FadeUp>
          <div style={{ maxWidth: 600, margin: '0 auto', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <FadeUp>
              <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.5rem', marginBottom: 32, textAlign: 'center' }}>Contact Details</h3>

              {[
                { label: 'Phone', value: <a href="tel:07763200412" style={{ color: 'var(--amber)', textDecoration: 'underline', textUnderlineOffset: 3, transition: 'color 0.3s ease' }}>07763 200412</a>, icon: <svg fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg> },
                { label: 'Email', value: <a href="mailto:jessie.bantock@outlook.com" style={{ color: 'var(--amber)', textDecoration: 'underline', textUnderlineOffset: 3, transition: 'color 0.3s ease' }}>jessie.bantock@outlook.com</a>, icon: <svg fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg> },
                { label: 'Office', value: <a href="https://www.google.com/maps/place/Bantock+%26+Co/@52.0706617,1.2075107,17z/data=!3m1!4b1!4m6!3m5!1s0x47d99fde9679c327:0x5929747ab5195509!8m2!3d52.0706617!4d1.2075107!16s%2Fg%2F11jmb0gbr2" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--amber)', textDecoration: 'underline', textUnderlineOffset: 3, transition: 'color 0.3s ease' }}>139 The Street, Rushmere St Andrew,<br/>Ipswich, Suffolk, IP5 1DG</a>, icon: <svg fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg> },
                { label: 'Availability', value: 'Monday – Friday, 9:00am – 5:00pm', icon: <svg fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg> },
              ].map(c => (
                <div key={c.label} style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
                  <div style={{
                    width: 44, height: 44, background: 'var(--white)', border: '1px solid var(--border)',
                    borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: 'var(--amber)',
                  }}>
                    <div style={{ width: 20, height: 20 }}>{c.icon}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-light)', fontWeight: 600, marginBottom: 2 }}>{c.label}</div>
                    <div style={{ color: 'var(--text-dark)', fontSize: '0.95rem', fontWeight: 500 }}>{c.value}</div>
                  </div>
                </div>
              ))}
            </FadeUp>
          </div>
        </div>
      </section>

      {/* Responsive overrides */}
      <style>{`
        @media (max-width: 968px) {
          .hero-grid { grid-template-columns: 1fr !important; text-align: center; }
          .hero-grid p { margin-left: auto; margin-right: auto; }
          .hero-grid > div:first-child > div:last-child { justify-content: center; }
          .hero-buttons { justify-content: center; }
          .hero-stats-desktop { display: none !important; }
          .hero-stats-mobile { display: flex !important; }
          .hero-visual { display: none !important; }
          .hero-grid > div:first-child { padding-bottom: 0 !important; }
          section:first-of-type { padding-bottom: 48px !important; }
          .services-grid { grid-template-columns: 1fr !important; }
          .about-grid { grid-template-columns: 1fr !important; gap: 48px !important; }
          .contact-grid { grid-template-columns: 1fr !important; gap: 40px !important; }
        }
        @media (max-width: 640px) {
          .services-grid { grid-template-columns: 1fr !important; }
          .hero-stats-mobile { justify-content: center !important; }
        }
        .service-card:hover {
          border-color: var(--amber-pale) !important;
          box-shadow: var(--shadow-md);
          transform: translateY(-2px);
        }
      `}</style>
    </>
  );
};

export default HomePage;